import { useState } from "react";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/lib/api";
import { invoiceMoney as money } from "@/lib/receivedInvoices";
import {
  basisReason,
  costCenter,
  receivedInvoices,
  splitReason,
  type Invoice,
  type InvoiceControl,
  type InvoiceSplit,
} from "@/lib/receivedInvoices";

export function InvoiceAllocationEditor({
  split,
  onChange,
  net,
  control,
  editable,
}: {
  split: InvoiceSplit[];
  onChange: (value: InvoiceSplit[]) => void;
  net: string | null;
  control: InvoiceControl;
  editable: boolean;
}) {
  const reason = splitReason(split, net);
  const change = (index: number, values: Partial<InvoiceSplit>) =>
    onChange(split.map((line, i) => (i === index ? { ...line, ...values } : line)));
  return (
    <div className="invoice-editor">
      <p>Raščlamba bez PDV-a · osnovica {net === null ? "nije upisana" : money(net)}</p>
      {split.map((line, index) => (
        <div className="invoice-editor-row" key={index} role="group" aria-label={`Redak raščlambe ${index + 1}`}>
          <label>
            Konto
            {editable ? (
              <select value={line.konto} onChange={(event) => change(index, { konto: event.target.value })}>
                <option value="">Odaberite</option>
                {[...new Set([...control.accounts, line.konto].filter(Boolean))].map((account) => (
                  <option key={account}>{account}</option>
                ))}
              </select>
            ) : (
              <span>{line.konto}</span>
            )}
          </label>
          <label>
            Gradilište
            {editable ? (
              <select
                value={line.gradiliste_id}
                onChange={(event) =>
                  change(index, {
                    gradiliste_id: event.target.value,
                    mjesto_troska: control.sites.find((site) => site.id === event.target.value)?.mjesto_troska || null,
                  })
                }
              >
                <option value="">Odaberite</option>
                {control.sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.naziv}
                  </option>
                ))}
              </select>
            ) : (
              <span>
                {control.sites.find((site) => site.id === line.gradiliste_id)?.naziv || "Gradilište nije dostupno"}
              </span>
            )}
          </label>
          <label>
            Mjesto troška
            <span>{costCenter(control.sites.find((site) => site.id === line.gradiliste_id)?.mjesto_troska ?? null)}</span>
          </label>
          <label>
            Iznos bez PDV-a (€)
            {editable ? (
              <input
                inputMode="decimal"
                value={line.iznos}
                onChange={(event) => change(index, { iznos: event.target.value })}
              />
            ) : (
              <span>{money(line.iznos)}</span>
            )}
          </label>
          {editable && (
            <Button
              variant="secondary"
              onClick={() => onChange(split.filter((_, i) => i !== index))}
              aria-label={`Ukloni redak ${index + 1}`}
            >
              Ukloni
            </Button>
          )}
        </div>
      ))}
      {editable && (
        <Button
          variant="secondary"
          onClick={() =>
            onChange([
              ...split,
              {
                konto: control.accounts[0] || "",
                gradiliste_id: control.sites[0]?.id || "",
                mjesto_troska: control.sites[0]?.mjesto_troska || null,
                iznos: "",
              },
            ])
          }
        >
          Dodaj redak raščlambe
        </Button>
      )}
      <p className="text-sm">Prazno mjesto troška: —. Raščlamba se sprema pri likvidaciji.</p>
      {reason && (
        <p role="status" className="text-red-800">
          {reason}
        </p>
      )}
    </div>
  );
}

export function ManualInvoiceBasis({
  invoice,
  disabled,
  onDirty,
  onSaved,
}: {
  invoice: Invoice;
  disabled: boolean;
  onDirty: (dirty: boolean) => void;
  onSaved: () => Promise<void>;
}) {
  const [net, setNet] = useState(invoice.amount_net || ""),
    [vat, setVat] = useState(invoice.vat_amount || "");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const reason = basisReason(net, vat, invoice.amount);
  const dirty = net !== (invoice.amount_net || "") || vat !== (invoice.vat_amount || "");
  const disabledReason = disabled
    ? "Spremanje nije dostupno dok traje druga radnja."
    : busy
      ? "Spremanje osnovice…"
      : reason || (!dirty ? "Osnovica i PDV već su spremljeni." : "");
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (disabledReason) return;
    setBusy(true);
    setError("");
    try {
      await receivedInvoices.basis(invoice, net, vat);
      await onSaved();
    } catch (cause) {
      setError(errorMessage(cause, "Osnovica nije spremljena."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={save} className="space-y-3" aria-label="Ručni unos osnovice">
      <p>Bez UBL-a osnovicu i PDV prepišite s računa. Iznos računa: {money(invoice.amount)}.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label>
          Osnovica bez PDV-a (€)
          <input
            inputMode="decimal"
            disabled={disabled || busy}
            value={net}
            onChange={(event) => {
              setNet(event.target.value);
              onDirty(event.target.value !== (invoice.amount_net || "") || vat !== (invoice.vat_amount || ""));
            }}
          />
        </label>
        <label>
          PDV (€)
          <input
            inputMode="decimal"
            disabled={disabled || busy}
            value={vat}
            onChange={(event) => {
              setVat(event.target.value);
              onDirty(net !== (invoice.amount_net || "") || event.target.value !== (invoice.vat_amount || ""));
            }}
          />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      <Button variant="secondary" type="submit" disabled={!!disabledReason} aria-describedby="basis-disabled-reason">
        Spremi osnovicu i PDV
      </Button>
      {disabledReason && (
        <p id="basis-disabled-reason" className="text-sm">
          {disabledReason}
        </p>
      )}
    </form>
  );
}
